import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { PopoverController } from '@ionic/angular'
import { MenuPopoverComponent } from '../../../components/menu-popover/menu-popover.component';
import { Collection } from '../../../models/collection';
import { LessonSong } from '../../../models/lesson-song';
import { Song } from '../../../models/song';
import { Teacher } from 'src/app/models/teacher';
import { Course } from 'src/app/models/course';
import { Lesson } from 'src/app/models/lesson';
import { instanceToInstance } from 'class-transformer';
import { Resources } from 'src/app/models/resources';
import { Common } from '../../../lib/common';
import { AudioProvider } from '../../../providers/audio/audio.provider';
import { PreferencesProvider } from '../../../providers/preferences/preferences.provider'
import { TeacherStore } from 'src/app/stores/teacher.store';
import { Subject, takeUntil } from 'rxjs';
import { ResourceStore } from 'src/app/stores/resources.store';
import { TeacherProvider } from 'src/app/providers/teacher/teacher.provider';
import { ClassType } from 'src/app/models/class-type';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-add-songs-slideout',
  templateUrl: './add-songs-slideout.component.html',
  styleUrls: ['./add-songs-slideout.component.scss']
})
export class AddSongsSlideoutComponent implements OnInit, OnDestroy {

  @Input() course: Course
  @Input() lesson: Lesson

  _addSongHandler: (lessonSong: LessonSong) => void = (() => {})

  @Input() set addSongHandler(handler: (lessonSong: LessonSong) => void) {
    this._addSongHandler = handler
    this.resetState()  // every time we bring up the component, go to main screen (TODO: can we hook into lifecycle for this??)
  }

  get addSongHandler(): (lessonSong: LessonSong) => void { return this._addSongHandler }

  mtLibrarySelected = true

  filter = {
    majorSelected: false,
    nonmajorSelected: false,
    dupleSelected: false,
    nondupleSelected: false,
    wordsSelected: false,
    swowSelected: false
  }
  
  ngUnsubscribeTeacher = new Subject<void>();
  ngUnsubscribeResources = new Subject<void>();

  teacher: Teacher; // = this.teacherProvider.teacher
  resources: Resources; // = this.resourcesProvider.resources

  selectedCollection: Collection = null
  searchText: string = ''
  sortOrder: string = 'Alphabetical'
  filtersVisible: boolean = false
  toggleFiltersText: string = ''
  detailsVisible: boolean = false

  get collections(): Collection[] {
    return Collection.sort(this.teacher?.collectionsFrom(this.resources?.collections || []) || [])
  }

  constructor(private popoverCtrl: PopoverController,
    private teacherProvider: TeacherProvider,
    private teacherStore: TeacherStore,
    private resourceStore: ResourceStore,
    private preferencesProvider: PreferencesProvider) {

  }

  ngOnInit() {
    this.resetState(true)

    this.teacher = this.teacherStore.getTeacher();
    this.teacherStore.state$
        .pipe(takeUntil(this.ngUnsubscribeTeacher))
        .subscribe(() => {
          this.teacher = this.teacherProvider.teacher;
          // this.teacher = this.teacherStore.getTeacher();
          // this.resetState(false)
        });

    this.resources = this.resourceStore.getResources();
    this.resourceStore.state$
        .pipe(takeUntil(this.ngUnsubscribeResources))
        .subscribe(() => {
          this.resources = this.resourceStore.getResources();
          this.selectedCollection = this.collections[0]
          if (this.course) {
            const collection = this.resources?.findCollection(this.course.collectionUuid)
            this.selectedCollection = collection || this.selectedCollection
          }
        });

    this.sortOrder = this.preferencesProvider.getAddSongSortOrderPref() || 'Alphabetical'
  }

  ngOnDestroy(): void {
    this.ngUnsubscribeTeacher.next(null);
    this.ngUnsubscribeTeacher.complete();

    this.ngUnsubscribeResources.next(null);
    this.ngUnsubscribeResources.complete();
  }

  resetState(hideDetails: boolean = true) {
    this.selectedCollection = this.collections[0]
    if (this.course) {
      const collection = this.resources?.findCollection(this.course.collectionUuid)
      this.selectedCollection = collection || this.selectedCollection
    }

    this.mtLibrarySelected = true

    this.filter = {
      majorSelected: false,
      nonmajorSelected: false,
      dupleSelected: false,
      nondupleSelected: false,
      wordsSelected: false,
      swowSelected: false
    }

    this.searchText = ''
    this.filtersVisible = false
    this.toggleFiltersText = 'Show Filters'

    this.detailsVisible = !hideDetails
    this.customLessonSong = LessonSong.defaults()
  }

  back() {
    this.detailsVisible = false
  }

  cancel() {
    this.addSongHandler(null)
  }

  addSong() {
    const lessonSong = LessonSong.createFrom(this.detailSong)
    this.addSongHandler(lessonSong)
  }

  selectMTLibrary() {
    this.mtLibrarySelected = true
  }

  selectCustomSong() {
    this.mtLibrarySelected = false
  }

  // MTLibrary Song

  trackBySongUuid(index: number, song: Song): string {
    return song.uuid;
  }

  anyFilterSelected(): boolean {
    return this.filter.majorSelected || this.filter.nonmajorSelected ||
    this.filter.dupleSelected || this.filter.nondupleSelected ||
    this.filter.wordsSelected || this.filter.swowSelected
  }

  get collectionSongs(): Song[] {
    return (this.selectedCollection ? this.selectedCollection.songs : [])
  }

  get displayedSongs(): Song[] {
    let songs = this.collectionSongs
    songs = this.searchSongs(songs)
    songs = this.filterSongs(songs)
    songs = this.sortSongs(songs)
    return songs
  }

  public trackItem (index: number, item: Song) {
    return item.uuid
  }

  private searchSongs(songs: Song[]): Song[] {
    if (this.searchText.length) {
      const searchText = this.searchText.toLowerCase()
      songs = songs.filter( (song: Song) => {
        return song.name.toLowerCase().indexOf(searchText) >= 0 ||
          song.tags.toLowerCase().indexOf(searchText) >= 0 ||
          (song.tonality || '').toLowerCase().indexOf(searchText) >= 0 ||
          (song.startingPitch || '').toLowerCase().indexOf(searchText) >= 0 ||
          (song.meter || '').toLowerCase().indexOf(searchText) >= 0 ||
          (song.isSwow ? ('swow'.indexOf(searchText) >= 0) : ('words'.indexOf(searchText) >= 0))
      })
    }
    return songs
  }

  private filterSongs(songs: Song[]): Song[] {
    // 309 -Song Selection Slider > If name of song = "Tonal Patterns" or "Rhythm Patterns" do not show in song list
    // See Teacher Edition collections for examples
    // const allowedRPTPPatterns = ['Rhythm Kids', 'bilingue-italiano', 'bilingue-espanol']

    const allowedRPTPPatterns = environment.rptpExclusionCollectionPatterns

    const selectedCollectionName = this.selectedCollection.name
    let allowRPTP: boolean = allowedRPTPPatterns.some((pattern: string) => {
      return selectedCollectionName.toLowerCase().includes(pattern.toLowerCase())
    }) 

    songs = songs.filter((song: Song) => {
      if (allowRPTP) {
        return !song.isInstrumental && !song.isSilentTrack
      }
      return !song.isTonalPatterns && !song.isRhythmPatterns && !song.isInstrumental && !song.isSilentTrack
    })

    if (this.anyFilterSelected()) {
      songs = songs.filter( (song: Song) => {
        let includeTonality = true
        if (this.filter.majorSelected) {
          includeTonality = song.isMajor
        } else if (this.filter.nonmajorSelected) {
          includeTonality = song.isNonmajor
        }

        let includeMeter = true
        if (this.filter.dupleSelected) {
          includeMeter = song.isDuple
        } else if (this.filter.nondupleSelected) {
          includeMeter = song.isNonduple
        }

        let includeWords = true
        if (this.filter.wordsSelected) {
          includeWords = !song.isSwow
        } if (this.filter.swowSelected) {
          includeWords = song.isSwow
        }

        return includeTonality && includeMeter && includeWords
      })
    }
    return songs
  }

  chooseSortOrder(event) {
    MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event, [
      { name: 'Alphabetical', action: () => { this.setSortOrder('Alphabetical')}},
      { name: 'Album Order', action: () => { this.setSortOrder('Album Order')}},
    ])
  }

  setSortOrder(order: string) {
    this.sortOrder = order
    this.preferencesProvider.setAddSongSortOrder(order)
  }

  private sortSongs(songs: Song[]): Song[] {
    if (this.sortOrder === 'Alphabetical') {
      songs = instanceToInstance(songs).sort((a: Song, b: Song) => a.name < b.name ? -1 : 1 )
    }
    return songs
  }

  chooseCollection(event) {
    if (this.collections.length > 0) {
      MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event,
        this.collections.map((collection: Collection) => {
          return {name: collection.name, action: () => { this.selectedCollection = collection}}
        })
      )
    }
  }

  toggleFilters() {
    this.filtersVisible = !this.filtersVisible
    this.toggleFiltersText = this.filtersVisible ? 'Hide Filters' : 'Show Filters'
  }

  clearFilters() {
    this.filter.majorSelected = false
    this.filter.nonmajorSelected = false
    this.filter.dupleSelected = false
    this.filter.nondupleSelected = false
    this.filter.wordsSelected = false
    this.filter.swowSelected = false
  }

  toggleMajor() {
    this.filter.majorSelected = !this.filter.majorSelected
    this.filter.nonmajorSelected = false
  }

  toggleNonMajor() {
    this.filter.nonmajorSelected = !this.filter.nonmajorSelected
    this.filter.majorSelected = false
  }

  toggleDuple() {
    this.filter.dupleSelected = !this.filter.dupleSelected
    this.filter.nondupleSelected = false
  }

  toggleNonDuple() {
    this.filter.nondupleSelected = !this.filter.nondupleSelected
    this.filter.dupleSelected = false
  }

  toggleWords() {
    this.filter.wordsSelected = !this.filter.wordsSelected
    this.filter.swowSelected = false
  }

  toggleSWOW() {
    this.filter.swowSelected = !this.filter.swowSelected
    this.filter.wordsSelected = false
  }

  addSelectedSong(song: Song) {
    const lessonSong = LessonSong.createFrom(song)
    this.addSongHandler(lessonSong)
  }

  detailSong: Song

  showDetails(song: Song) {
    this.detailSong = song
    this.detailsVisible = true
  }

  // Custom Song

  customLessonSong: LessonSong = LessonSong.defaults()

  chooseKey(event) {
    const keys = ['Ab', 'A', 'Bb', 'B', 'C', 'C#', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'Gb', 'G']
    MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event,
      keys.map((key: string) => {
        return {name: key, action: () => { this.customLessonSong.key = key }}
      }), 90
    )
  }

  chooseMeter(event) {
    const meters = ['Duple', 'Triple', 'Duple/Triple', 'Swing', 'Asymmetric', 'Other']
    MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event,
      meters.map((meter: string) => {
        return {name: meter, action: () => { this.customLessonSong.meter = meter }}
      })
    )
  }

  chooseTonality(event) {
    const tonalities = ['Major', 'minor', 'dorian', 'phrygian', 'Lydian', 'Mixolydian', 'aeolian', 'Other']
    MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event,
      tonalities.map((tonality: string) => {
        return {name: tonality, action: () => { this.customLessonSong.tonality = tonality }}
      })
    )
  }

  chooseStartingPitch(event) {
    const keys = ['Ab', 'A', 'Bb', 'B', 'C', 'C#', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'Gb', 'G']
    MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event,
      keys.map((key: string) => {
        return {name: key, action: () => { this.customLessonSong.startingPitch = key }}
      }), 90
    )
  }

  chooseHasWords(event) {
    const words = ['SWOW', 'Words']
    MenuPopoverComponent.present(this.popoverCtrl, 'lesson-plans-popover', event,
      words.map((word: string) => {
        return {name: word, action: () => { this.customLessonSong.isSwow = (word === 'SWOW') }}
      }), 140
    )
  }

  addCustomSong() {
    this.addSongHandler(this.customLessonSong)
  }

  playCustomSong() {
    Common.openOutsideUrl(this.customLessonSong.url)
  }
}
